import { Board } from './Board'
import { BOARD_COLS, BOARD_ROWS, CELL_EMPTY, CELL_GARBAGE } from '../constants'

export interface Group {
  color: number
  cells: Array<[number, number]>
}

/**
 * Find all connected groups of 4+ same-colored (non-garbage) cells.
 * Uses BFS flood-fill per unvisited cell.
 */
export function detectGroups(board: Board): Group[] {
  const visited = new Uint8Array(BOARD_COLS * BOARD_ROWS)
  const groups: Group[] = []

  for (let row = 0; row < BOARD_ROWS; row++) {
    for (let col = 0; col < BOARD_COLS; col++) {
      const idx = row * BOARD_COLS + col
      if (visited[idx]) continue

      const color = board.get(col, row)
      if (color === CELL_EMPTY || color === CELL_GARBAGE) continue

      // BFS
      const group: Group = { color, cells: [] }
      const queue: Array<[number, number]> = [[col, row]]
      visited[idx] = 1

      while (queue.length > 0) {
        const [c, r] = queue.shift()!
        group.cells.push([c, r])

        for (const [nc, nr] of neighbors(c, r)) {
          if (!board.inBounds(nc, nr)) continue
          const nidx = nr * BOARD_COLS + nc
          if (visited[nidx]) continue
          if (board.get(nc, nr) === color) {
            visited[nidx] = 1
            queue.push([nc, nr])
          }
        }
      }

      if (group.cells.length >= 4) {
        groups.push(group)
      }
    }
  }

  return groups
}

/**
 * Return the unique garbage cells that are orthogonally adjacent to any
 * cell in the given groups.
 */
export function findAdjacentGarbage(
  board: Board,
  groups: Group[],
): Array<[number, number]> {
  const seen = new Set<number>()
  const result: Array<[number, number]> = []

  for (const group of groups) {
    for (const [c, r] of group.cells) {
      for (const [nc, nr] of neighbors(c, r)) {
        if (!board.inBounds(nc, nr)) continue
        if (board.get(nc, nr) !== CELL_GARBAGE) continue
        const key = nr * BOARD_COLS + nc
        if (!seen.has(key)) {
          seen.add(key)
          result.push([nc, nr])
        }
      }
    }
  }

  return result
}

function neighbors(col: number, row: number): Array<[number, number]> {
  return [
    [col - 1, row],
    [col + 1, row],
    [col, row - 1],
    [col, row + 1],
  ]
}
